import { AIBadgeScore, Button, Card, Drawer, Text } from "components/common"
import { useLazyGetWhatsappChatInsightsQuery } from "features/clients/detail/api"
import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { useTranslation } from "react-i18next"
import { localisedValue } from "utils"
import { Separator } from "components/custom"
import { Flex } from "antd"
import { ChatSummary } from "../chatSummary/chatSummary"
import ChatInsights from "../chatInsights/chatInsights"
import styles from "../../index.module.less"
import cx from "clsx"
import BestTimeCard from "../BestTimeCard/BestTimeCard"
import { getAppLanguage } from "configs/useInitializeApp"

const WhatsappChatInsights = forwardRef(({ selectedStaff, client }, ref) => {
  const { t } = useTranslation()
  const language = getAppLanguage()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [tab, setTab] = useState("summary")
  const [getInsights, { data, isFetching }] = useLazyGetWhatsappChatInsightsQuery()

  useImperativeHandle(ref, () => ({
    showDrawer: () => setIsDrawerOpen(true),
  }))

  useEffect(() => {
    if (!isDrawerOpen) return
    selectedStaff?.id && getInsights({ chatId: selectedStaff?.id })
  }, [isDrawerOpen, selectedStaff])

  const resetToDefault = () => {
    setIsDrawerOpen(false)
    setTab("summary")
  }

  const tabItems = () => [
    {
      tab: t("Summary"),
      key: "summary",
    },
    {
      tab: t("Insights"),
      key: "insights",
    },
  ]

  const getTabContent = () => ({
    summary: <ChatSummary loading={isFetching} summary={data?.summary} />,
    insights: <ChatInsights loading={isFetching} insights={data?.insights} />,
  })

  const getDrawerHeader = () => (
    <Flex gap={8}>
      <Button
        type="muted"
        shape="circle"
        icon={language?.rtl ? "HiArrowRight" : "HiArrowLeft"}
        onClick={resetToDefault}
        style={{ alignSelf: "center" }}
      />
      <Flex vertical>
        <Text style={{ fontSize: 18 }}>
          <Text style={{ fontSize: 18 }} type="secondary">
            {localisedValue({ object: selectedStaff?.User, key: "name" })} {t("talking to")}
          </Text>{" "}
          <strong> {localisedValue({ object: data?.header, key: "clientName" })} </strong>
        </Text>
        <Flex align={"center"}>
          <AIBadgeScore
            totalScore={data?.header?.overallEngagementScore}
            title={"Score:"}
            titleClassName={"fs12"}
            padding={"pi-0"}
            buttonProps={{
              style: { height: 20 },
              size: "small",
              shape: "round",
              color: "#FAFAFA",
            }}
            iconProps={{ size: "14px" }}
          />
          <Text className={"fs12"} type="secondaryLight">
            <Separator />
            {t("Last message:")} {data?.header?.lastMessage}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )

  return (
    <Drawer
      title={getDrawerHeader()}
      placement="right"
      open={isDrawerOpen}
      onClose={resetToDefault}
      width={900}
      footer={null}
      bodyStyle={{ padding: 28 }}
    >
      <BestTimeCard
        bestTime={data?.bestMessageHour}
        title={data?.isCurrentHourBest ? t("Message Now!") : t("Best Time to Message!")}
        description={
          data?.isCurrentHourBest
            ? t("Excellent opportunity! Client is the most responsive at this time. Message now")
            : t(
                "AI predicts client is most likely to reply to messages during this window. Plan your follow-up accordingly."
              )
        }
        loading={isFetching}
      />
      <Card
        tabList={tabItems()}
        activeTabKey={tab}
        onTabChange={key => {
          setTab(key)
        }}
        headStyle={{ padding: 0 }}
        bodyStyle={{ padding: 0 }}
        className={cx(styles.basicCard, styles.tabCard)}
      />
      {getTabContent()[tab]}
    </Drawer>
  )
})

export default WhatsappChatInsights
